// Geolocation utilities for commercial real estate search - Ellis Reed

export interface UserLocation {
  lat: number;
  lng: number;
  city?: string;
  state?: string;
  metroplex?: string; // Populated server-side via DB metro lookup
  timestamp: number;
}

// Get user's geolocation from browser
export async function getUserGeolocation(): Promise<UserLocation | null> {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(null);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const location: UserLocation = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          // metroplex is resolved server-side via DB metro lookup, not client-side
          timestamp: Date.now(),
        };
        resolve(location);
      },
      (error) => {
        console.log('[GEOLOCATION] User denied or error:', error.message);
        resolve(null);
      },
      {
        enableHighAccuracy: false, // Faster, less battery drain
        timeout: 10000,
        maximumAge: 300000, // 5 minutes cache
      }
    );
  });
}

// Session storage keys
const SESSION_KEY = 'usland_user_location';
const PREFERENCE_KEY = 'usland_location_preference';

/**
 * Check if sessionStorage is available
 * Prevents crashes in Safari private mode and during SSR
 * @author Ellis Reed
 */
function isSessionStorageAvailable(): boolean {
  // SSR guard - sessionStorage only exists in browser
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    // Check if sessionStorage exists
    if (!window.sessionStorage) {
      return false;
    }

    // Test if we can actually use it (Safari private mode blocks this)
    const testKey = '__usland_storage_test__';
    window.sessionStorage.setItem(testKey, 'test');
    window.sessionStorage.removeItem(testKey);
    return true;
  } catch (error) {
    // Safari private mode or other storage restrictions
    console.warn('[GEOLOCATION] sessionStorage not available:', error);
    return false;
  }
}

/**
 * Check if localStorage is available
 * Prevents crashes in Safari private mode and during SSR
 * @author Ellis Reed
 */
function isLocalStorageAvailable(): boolean {
  // SSR guard - localStorage only exists in browser
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    // Check if localStorage exists
    if (!window.localStorage) {
      return false;
    }

    // Test if we can actually use it (Safari private mode blocks this)
    const testKey = '__usland_storage_test__';
    window.localStorage.setItem(testKey, 'test');
    window.localStorage.removeItem(testKey);
    return true;
  } catch (error) {
    // Safari private mode or other storage restrictions
    console.warn('[GEOLOCATION] localStorage not available:', error);
    return false;
  }
}

// Save location to session storage
export function saveLocationToSession(location: UserLocation): void {
  if (!isSessionStorageAvailable()) {
    console.warn('[GEOLOCATION] sessionStorage not available, skipping save');
    return;
  }

  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(location));
  } catch (error) {
    console.error('[GEOLOCATION] Failed to save to session:', error);
  }
}

// Get location from session storage
export function getLocationFromSession(): UserLocation | null {
  if (!isSessionStorageAvailable()) {
    console.warn('[GEOLOCATION] sessionStorage not available, skipping read');
    return null;
  }

  try {
    const stored = sessionStorage.getItem(SESSION_KEY);
    if (!stored) return null;

    const location = JSON.parse(stored) as UserLocation;

    // Check if location is stale (older than 1 hour)
    if (Date.now() - location.timestamp > 3600000) {
      sessionStorage.removeItem(SESSION_KEY);
      return null;
    }

    return location;
  } catch (error) {
    console.error('[GEOLOCATION] Failed to read from session:', error);
    return null;
  }
}

// Save user preference (accepted/declined)
export function saveLocationPreference(accepted: boolean): void {
  if (!isLocalStorageAvailable()) {
    console.warn('[GEOLOCATION] localStorage not available, skipping save');
    return;
  }

  try {
    localStorage.setItem(PREFERENCE_KEY, accepted ? 'accepted' : 'declined');
  } catch (error) {
    console.error('[GEOLOCATION] Failed to save preference:', error);
  }
}

// Get user preference
export function getLocationPreference(): 'accepted' | 'declined' | null {
  if (!isLocalStorageAvailable()) {
    console.warn('[GEOLOCATION] localStorage not available, skipping read');
    return null;
  }

  try {
    const pref = localStorage.getItem(PREFERENCE_KEY);
    return pref as 'accepted' | 'declined' | null;
  } catch (error) {
    return null;
  }
}

// Clear all location data
export function clearLocationData(): void {
  // Clear sessionStorage if available
  if (isSessionStorageAvailable()) {
    try {
      sessionStorage.removeItem(SESSION_KEY);
    } catch (error) {
      console.error('[GEOLOCATION] Failed to clear session data:', error);
    }
  }

  // Clear localStorage if available
  if (isLocalStorageAvailable()) {
    try {
      localStorage.removeItem(PREFERENCE_KEY);
    } catch (error) {
      console.error('[GEOLOCATION] Failed to clear local data:', error);
    }
  }
}

// Cityscape lookup is now fully server-side via S3 presigned URLs
// See: src/app/actions/s3-cityscape.ts → getCityscapeForLocation()
